import Message from "@/components/message";
import { queue } from "@/rq";

const getInitialState = () => {
  return {
    instruction: "",
    number_of_words: 1000,
    language: localStorage.getItem("lang") == "en" ? "en-US" : "zh-CN",
    file_type: "docx",
    fileName: "",
    template: "aaai2025",
    email: "",
    llm: "",
    diagram: ["taxonomy"],
    appendix: [],
    sources: ["en-US"],
    plugins: ["enable_minimal_references"],
    customReferences: [],
    fileUploadPending: false,
  };
};

const state = getInitialState();

// Constants - 更新为与Python一致
const DEFAULT_MAX_CHILDREN = 3;
const EPOCH_1_MAX_CHILDREN = 6;

// Helper functions
const calculateEpochs = (numberOfWords, limit = 1000) => {
  if (numberOfWords < limit) {
    return 1;
  }

  let epochs = 1;
  let currentWords = numberOfWords;

  // First epoch uses EPOCH_1_MAX_CHILDREN
  if (currentWords >= limit) {
    currentWords = Math.floor(currentWords / EPOCH_1_MAX_CHILDREN);
    epochs += 1;
  }

  // Subsequent epochs use DEFAULT_MAX_CHILDREN
  while (currentWords >= limit) {
    currentWords = Math.floor(currentWords / DEFAULT_MAX_CHILDREN);
    epochs += 1;
  }

  return epochs;
};

const calculateLengthFactor = (words, maxWords = 500, baseline = 250) => {
  // Cap words at maxWords
  const effectiveWords = Math.min(words, maxWords);

  // Linear interpolation
  if (effectiveWords <= baseline) {
    // 0 to baseline: 0.8x to 1x
    return 0.8 + (0.2 * effectiveWords / baseline);
  } else {
    // baseline to maxWords: 1x to 1.2x
    return 1.0 + (0.2 * (effectiveWords - baseline) / (maxWords - baseline));
  }
};

const calculateTasksInEpoch = (epoch) => {
  if (epoch === 1) {
    return 1;
  } else if (epoch === 2) {
    return EPOCH_1_MAX_CHILDREN;
  } else {
    // For epoch 3 and beyond
    return EPOCH_1_MAX_CHILDREN * Math.pow(DEFAULT_MAX_CHILDREN, epoch - 2);
  }
};

const calculateWordsPerTask = (epoch, totalWords) => {
  const tasksInEpoch = calculateTasksInEpoch(epoch);
  return Math.floor(totalWords / tasksInEpoch);
};

const calculateParallelTime = (totalTasks, maxConcurrent, timePerTask, parallelOverhead = 1.0) => {
  if (totalTasks === 0) {
    return 0;
  }
  const batches = Math.ceil(totalTasks / maxConcurrent);
  return batches * timePerTask * parallelOverhead;
};

const estimateRuntime = (settings, parallelOverhead = 1.5, safetyFactor = 1.25, verbose = false) => {
  // Basic parameters
  const numberOfWords = settings.number_of_words || 500;
  const maxConcurrent = settings.max_concurrent || 3;
  const enableFullText = settings.enable_full_text;
  const enableMinimalReferences = settings.enable_minimal_references;
  const customSources = settings.custom_sources || ["en-US"];
  const iterationsQuery = settings.iterations_query || 1;
  const iterationsOrgnizer = settings.iterations_orgnizer || 1;
  const iterationsNote = settings.iterations_note || 1;
  const iterationsNarrative = settings.iterations_narrative || 1;
  const diagram = settings.diagram || [];

  // Calculate actual epochs needed
  const actualEpochs = calculateEpochs(numberOfWords);

  // Base execution time for each agent (seconds) - 更新为与Python一致
  const agentTimes = {
    "query_agent_en": 3.5,
    "query_agent_zh": 30,  // 从40改为30
    "scraper_agent": 3.5,
    "orgnizer_agent": 6.0,  // 从5.5改为6.0
    "note_agent": 15.0,
    "collection_agent": 4.5,
    "narrative_agent": 6.5,  // 从4.5改为6.5
    "inherit_agent": 2.0,
    "metadata_agent": 4,
    "diagram_agent": 40
  };

  // Define which agents are affected by length factor
  const lengthAffectedAgents = new Set([
    "orgnizer_agent",
    "note_agent",
    "narrative_agent",
    "collection_agent"
  ]);

  let totalTime = 0;

  // Calculate time for each epoch
  for (let epoch = 1; epoch <= actualEpochs; epoch++) {
    // Calculate number of tasks in current epoch
    const tasksInEpoch = calculateTasksInEpoch(epoch);

    // Calculate words per task in this epoch
    const wordsPerTask = calculateWordsPerTask(epoch, numberOfWords);
    const lengthFactor = calculateLengthFactor(wordsPerTask);

    // Query Agent
    if (customSources.includes("en-US")) {
      const time = calculateParallelTime(
        tasksInEpoch,
        maxConcurrent,
        agentTimes["query_agent_en"] * iterationsQuery,
        parallelOverhead
      );
      totalTime += time;
    }

    if (customSources.includes("zh-CN")) {
      const time = calculateParallelTime(
        tasksInEpoch,
        maxConcurrent,
        agentTimes["query_agent_zh"] * iterationsQuery,
        parallelOverhead
      );
      totalTime += time;
    }

    // Scraper Agent (only when enable_full_text is true)
    if (enableFullText) {
      const time = calculateParallelTime(
        tasksInEpoch,
        maxConcurrent,
        agentTimes["scraper_agent"],
        parallelOverhead
      );
      totalTime += time;
    }

    // Organizer Agent (affected by length factor)
    let agentTime = agentTimes["orgnizer_agent"] * iterationsOrgnizer;
    if (lengthAffectedAgents.has("orgnizer_agent")) {
      agentTime *= lengthFactor;
    }
    const orgnizerTime = calculateParallelTime(
      tasksInEpoch,
      maxConcurrent,
      agentTime,
      parallelOverhead
    );
    totalTime += orgnizerTime;

    // Note Agent (only when enable_full_text is true, affected by length factor)
    if (enableFullText) {
      let noteAgentTime = agentTimes["note_agent"] * iterationsNote;
      if (lengthAffectedAgents.has("note_agent")) {
        noteAgentTime *= lengthFactor;
      }
      const time = calculateParallelTime(
        tasksInEpoch,
        maxConcurrent,
        noteAgentTime,
        parallelOverhead
      );
      totalTime += time;
    }

    // Collection Agent (only when enable_minimal_references is true, affected by length factor)
    if (enableMinimalReferences) {
      let collectionAgentTime = agentTimes["collection_agent"];
      if (lengthAffectedAgents.has("collection_agent")) {
        collectionAgentTime *= lengthFactor;
      }
      const time = calculateParallelTime(
        tasksInEpoch,
        maxConcurrent,
        collectionAgentTime,
        parallelOverhead
      );
      totalTime += time;
    }

    // Narrative Agent (affected by length factor)
    let narrativeAgentTime = agentTimes["narrative_agent"] * iterationsNarrative;
    if (lengthAffectedAgents.has("narrative_agent")) {
      narrativeAgentTime *= lengthFactor;
    }
    const narrativeTime = calculateParallelTime(
      tasksInEpoch,
      maxConcurrent,
      narrativeAgentTime,
      parallelOverhead
    );
    totalTime += narrativeTime;

    // Inherit Agent (except for the last epoch)
    if (epoch < actualEpochs) {
      const time = calculateParallelTime(
        tasksInEpoch,
        maxConcurrent,
        agentTimes["inherit_agent"],
        parallelOverhead
      );
      totalTime += time;
    }
  }

  // Metadata Agent (runs once after all epochs)
  const totalMetadataTasks = calculateTasksInEpoch(actualEpochs);
  const metadataTime = calculateParallelTime(
    totalMetadataTasks,
    maxConcurrent,
    agentTimes["metadata_agent"],
    parallelOverhead
  );
  totalTime += metadataTime;

  // Diagram Agent (if needed, no parallel execution)
  if (diagram.includes("taxonomy")) {
    totalTime += agentTimes["diagram_agent"];
  }

  // 应用安全系数
  totalTime = totalTime * safetyFactor;

  return totalTime;
};

const getters = {
  fileName (state) {
    return state.fileName
  },
  instruction (state) {
    return state.instruction;
  },
  numberOfWords (state) {
    return state.number_of_words;
  },
  language (state) {
    return state.language;
  },
  fileType (state) {
    return state.file_type;
  },
  template (state) {
    return state.template;
  },
  email (state) {
    return state.email;
  },
  diagram (state) {
    return state.diagram;
  },
  appendix (state) {
    return state.appendix;
  },
  sources (state) {
    return state.sources;
  },
  plugins (state) {
    return state.plugins;
  },
  customReferences (state) {
    return state.customReferences;
  },
  settings (state) {
    const today = new Date();
    const isJune = today.getMonth() === 5;
    const isPromoDay = isJune && (today.getDate() === 15 || today.getDate() === 16);

    const llm = isPromoDay ? "aHsXePN2NGozFQ==" : (state.llm || "");

    const filteredSources = state.sources.filter(source => source !== 'custom');
    const custom_references = state.customReferences.map(ref => ref.title);

    const output = {
      instruction: state.instruction,
      number_of_words: state.number_of_words,
      language: state.language,
      file_type: state.file_type,
      template: state.template,
      email: state.email,
      diagram: state.diagram,
      appendix: state.appendix,
      llm: llm,
      custom_references: custom_references,
      custom_sources: filteredSources,
      enable_full_text: state.plugins.includes('enable_full_text'),
      enable_minimal_references: state.plugins.includes('enable_minimal_references'),
      iterations_query: state.plugins.includes('iterations_query') ? 2 : 1,
      iterations_orgnizer: state.plugins.includes('iterations_orgnizer') ? 2 : 1,
      iterations_note: state.plugins.includes('iterations_note') ? 2 : 1,
      iterations_narrative: state.plugins.includes('iterations_narrative') ? 2 : 1,
    };

    return output;
  },
  waitTime (state, getters) {
    // Get settings from the settings getter
    const settings = getters.settings;

    // Add max_concurrent to settings (default 3)
    const settingsWithConcurrent = {
      ...settings,
      max_concurrent: 3
    };

    // Calculate runtime using the estimateRuntime function
    // 使用 parallelOverhead=1.5 和 safetyFactor=1.25
    const totalSeconds = estimateRuntime(settingsWithConcurrent, 1.5, 1.25, false);

    // 不再需要额外的20%安全边际，因为已经在estimateRuntime中应用了safetyFactor
    return Math.ceil(totalSeconds);
  },
};

const mutations = {
  updateFileName (state, { fileName }) {
    state.fileName = fileName
  },
  updateInstruction (state, { instruction }) {
    state.instruction = instruction;
  },
  updateNumSections (state, { num_sections }) {
    state.num_sections = num_sections;
  },
  updateNumWords (state, { number_of_words }) {
    state.number_of_words = number_of_words;
  },
  updateNumAbstractWords (state, { num_abstract_words }) {
    state.num_abstract_words = num_abstract_words;
  },
  updateNumCitations (state, { num_citations }) {
    state.num_citations = num_citations;
  },
  updateLanguage (state, { language }) {
    state.language = language;
  },
  updateFileType (state, { file_type }) {
    state.file_type = file_type;
  },
  updateTemplate (state, { template }) {
    state.template = template;
  },
  updateAppendix (state, { appendix }) {
    state.appendix = appendix;
  },
  updateSources (state, { sources }) {
    state.sources = sources;
  },
  updatePlugins (state, { plugins }) {
    state.plugins = plugins;
  },
  updateDiagram (state, { diagram }) {
    state.diagram = diagram;
  },
  updateCustomReferences (state, { customReferences }) {
    // console.log(customReferences);
    state.customReferences = customReferences;
  },
  updateEmail (state, { email }) {
    state.email = email;
  },
  dropEmail (state) {
    state.email = "";
  },
  updateFileUploadPending (state, { fileUploadPending }) {
    state.fileUploadPending = fileUploadPending
  }
};

const actions = {
  async updateInstructionFromFile ({ commit }, { file, fileType }) {
    const formData = new FormData()
    formData.append("file", file);
    // formData.append("target_method", fileType)

    try {
      commit('updateFileUploadPending', { fileUploadPending: true })

      let url = "";
      let pollUrl = "";

      if (fileType === "pdf") {
        url = "/api/extract_pdf_text_queue";
        pollUrl = "/api/extract_pdf_text_result/{taskId}";
      } else if (fileType === "docx") {
        url = "/api/extract_docx_text_queue";
        pollUrl = "/api/extract_docx_text_result/{taskId}";
      } else {
        url = "/api/extract_image_text_queue";
        pollUrl = "/api/extract_image_text_result/{taskId}";
      }

      const response = await queue({
        url: url,
        method: "post",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        }
      }).poll({
        url: pollUrl,
        options: {
          maxPolls: 600,
          interval: 1000,
        },
      });

      commit('updateFileUploadPending', { fileUploadPending: false })
      const instruction = response.data
      commit("updateFileName", { fileName: file.name })
      commit("updateInstruction", { instruction: instruction })
      return instruction
    } catch (error) {
      commit('updateFileUploadPending', { fileUploadPending: false })
      commit("updateInstruction", { instruction: "" })
      console.error(`Service error (${error.code}):`, error.message);
      Message.text(error?.error?.response?.statusText || error?.message || '未知错误')
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};